import { Textarea } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Field } from '@/components/ui/field';
import { useAddPayoutNoteAction } from '@/hooks/actions/use-add-payout-note-action';

interface PayoutNoteFormProps {
  payoutId: string;
  defaultValue?: string;
  onSuccess?: () => void;
}

const DEBOUNCE_MS = 800;

const PayoutNoteForm: React.FC<PayoutNoteFormProps> = ({
  payoutId,
  defaultValue = '',
  onSuccess,
}) => {
  const { t } = useTranslation();

  const payoutNoteSchema = z.object({
    note: z.string().max(1000, t('payouts.notes.tooLong')),
  });

  type PayoutNoteFormData = z.infer<typeof payoutNoteSchema>;

  const methods = useForm<PayoutNoteFormData>({
    resolver: zodResolver(payoutNoteSchema),
    defaultValues: { note: defaultValue },
  });
  const { mutateAsync } = useAddPayoutNoteAction();
  const noteValue = useWatch({ control: methods.control, name: 'note' });
  const lastSubmitted = useRef<string>(defaultValue);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    methods.reset({ note: defaultValue });
    lastSubmitted.current = defaultValue;
  }, [defaultValue, methods]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (noteValue === lastSubmitted.current) return;
    debounceRef.current = setTimeout(() => {
      methods.handleSubmit(async (data) => {
        await mutateAsync({ payoutId, note: data.note });
        lastSubmitted.current = data.note;
        if (onSuccess) onSuccess();
      })();
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteValue, payoutId]);

  return (
    <FormProvider {...methods}>
      <form>
        <Field
          gapY={3}
          label={t('payouts.notes.label')}
          labelProps={{ fontSize: '17px' }}
          invalid={!!methods.formState.errors.note}
        >
          <Textarea
            {...methods.register('note')}
            minH="120px"
            placeholder={t('payouts.notes.placeholder')}
            rounded="lg"
            p={3}
          />
        </Field>
      </form>
    </FormProvider>
  );
};

export default PayoutNoteForm;
